import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
  CpuChipIcon,
  CloudIcon,
  ShieldCheckIcon,
  CogIcon,
  RocketLaunchIcon,
  CircleStackIcon,
  CubeTransparentIcon,
  BuildingOffice2Icon,
  ArrowPathIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';

import ecommerceImg from '../../assets/img/Recent/ecommerce.png';
import cloudImg from '../../assets/img/Recent/cloud.png';
import mobileImg from '../../assets/img/Recent/Mobilebanking.png';
import analyticsImg from '../../assets/img/Recent/data analysis.png';
import programmingImg from '../../assets/img/Recent/Programing.png';
import cloudMigrationImg from '../../assets/img/Recent/cloud migration.png';

const services = [
  { name: 'AI & Machine Learning', desc: 'Intelligent automation, predictive models, and AI-driven decision systems.', icon: CpuChipIcon, href: '/services' },
  { name: 'Cloud Infrastructure', desc: 'Scalable multi-cloud architectures with high availability and resilience.', icon: CloudIcon, href: '/cloud-solutions' },
  { name: 'Cybersecurity', desc: 'Zero-trust security, threat detection, and enterprise compliance frameworks.', icon: ShieldCheckIcon, href: '/cybersecurity' },
  { name: 'DevOps Automation', desc: 'CI/CD pipelines, infrastructure as code, and automated deployments.', icon: CogIcon, href: '/services' },
  { name: 'SaaS Development', desc: 'Cloud-native SaaS platforms built for scale, security, and growth.', icon: RocketLaunchIcon, href: '/services' },
  { name: 'Data Engineering', desc: 'Modern data pipelines, warehouses, and real-time analytics platforms.', icon: CircleStackIcon, href: '/services' },
  { name: 'Blockchain Solutions', desc: 'Secure distributed ledgers and decentralized enterprise applications.', icon: CubeTransparentIcon, href: '/services' },
  { name: 'Enterprise Software', desc: 'Custom ERP, CRM, and mission-critical business applications.', icon: BuildingOffice2Icon, href: '/services' },
  { name: 'Digital Transformation', desc: 'End-to-end modernization strategies for future-ready organizations.', icon: ArrowPathIcon, href: '/services' },
];

const techStack = [
  { name: 'AI/ML', category: 'Intelligence', glow: 'shadow-blue-500/20', border: 'border-blue-200/80', icon: '🧠' },
  { name: 'Kubernetes', category: 'Orchestration', glow: 'shadow-cyan-500/20', border: 'border-cyan-200/80', icon: '⎈' },
  { name: 'Docker', category: 'Containers', glow: 'shadow-sky-500/20', border: 'border-sky-200/80', icon: '🐳' },
  { name: 'AWS', category: 'Cloud', glow: 'shadow-amber-500/20', border: 'border-amber-200/80', icon: '☁️' },
  { name: 'Azure', category: 'Cloud', glow: 'shadow-blue-500/20', border: 'border-blue-200/80', icon: '◆' },
  { name: 'Google Cloud', category: 'Cloud', glow: 'shadow-emerald-500/20', border: 'border-emerald-200/80', icon: '◎' },
  { name: 'TensorFlow', category: 'AI Framework', glow: 'shadow-orange-500/20', border: 'border-orange-200/80', icon: '⚡' },
  { name: 'PyTorch', category: 'AI Framework', glow: 'shadow-rose-500/20', border: 'border-rose-200/80', icon: '🔥' },
  { name: 'Blockchain', category: 'Web3', glow: 'shadow-violet-500/20', border: 'border-violet-200/80', icon: '🔗' },
  { name: 'Big Data', category: 'Analytics', glow: 'shadow-indigo-500/20', border: 'border-indigo-200/80', icon: '📊' },
  { name: 'Cybersecurity', category: 'Security', glow: 'shadow-red-500/20', border: 'border-red-200/80', icon: '🛡️' },
  { name: 'DevSecOps', category: 'Pipeline', glow: 'shadow-teal-500/20', border: 'border-teal-200/80', icon: '🚀' },
];

const caseStudies = [
  { title: 'AI Analytics Command Center', type: 'AI Dashboard', desc: 'Real-time ML insights platform with predictive forecasting and executive dashboards.', image: analyticsImg, tags: ['TensorFlow', 'Python', 'AWS'], span: 'lg:col-span-2 lg:row-span-2' },
  { title: 'Secure Fintech Banking UI', type: 'Fintech', desc: 'Next-gen mobile banking with biometric auth, fraud detection, and PCI-DSS compliance.', image: mobileImg, tags: ['React Native', 'Security'], span: '' },
  { title: 'Healthcare Management Suite', type: 'Healthcare', desc: 'Unified patient records, scheduling, and hospital network operations platform.', image: programmingImg, tags: ['HL7', 'Cloud'], span: '' },
  { title: 'SaaS Cloud Monitoring', type: 'SaaS Dashboard', desc: 'Multi-tenant observability with live metrics, alerts, and infrastructure health views.', image: cloudImg, tags: ['Kubernetes', 'Grafana'], span: 'lg:col-span-2' },
  { title: 'Enterprise E-Commerce', type: 'Retail Tech', desc: 'High-traffic commerce platform with AI recommendations and global payment rails.', image: ecommerceImg, tags: ['Node.js', 'Redis'], span: '' },
  { title: 'Cybersecurity Operations', type: 'Security Ops', desc: 'SOC dashboard with threat intelligence, SIEM integration, and incident response.', image: cloudMigrationImg, tags: ['Fortinet', 'DevSecOps'], span: '' },
];

function SectionHeader({ badge, title, highlight, subtitle }) {
  return (
    <div className="text-center mb-14 md:mb-16">
      {badge && (
        <span className="inline-block px-4 py-1.5 mb-4 text-xs font-semibold tracking-widest uppercase rounded-full hero-glass-tag text-[#2563EB]">
          {badge}
        </span>
      )}
      <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight mb-4">
        {title} <span className="bg-gradient-to-r from-[#2563EB] to-cyan-500 bg-clip-text text-transparent">{highlight}</span>
      </h2>
      {subtitle && <p className="text-lg text-slate-600 max-w-3xl mx-auto leading-relaxed">{subtitle}</p>}
    </div>
  );
}

export function ServicesSection() {
  const [ref, inView] = useInView({ threshold: 0.08, triggerOnce: true });

  return (
    <section ref={ref} className="py-24 md:py-28 px-6 bg-gradient-to-b from-white via-slate-50/80 to-white relative overflow-hidden">
      <div className="absolute top-0 right-0 w-96 h-96 bg-blue-400/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-cyan-300/10 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto relative">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5 }}>
          <SectionHeader
            badge="What We Offer"
            title="Advanced"
            highlight="IT Services"
            subtitle="Enterprise-grade solutions engineered with precision, security, and scalability at every layer."
          />
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {services.map((service, i) => {
            const Icon = service.icon;
            return (
              <motion.div
                key={service.name}
                initial={{ opacity: 0, y: 24 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: i * 0.06, duration: 0.45 }}
                whileHover={{ y: -6 }}
                className="group relative"
              >
                <div className="absolute -inset-px rounded-2xl bg-gradient-to-br from-[#2563EB]/0 via-cyan-400/0 to-[#2563EB]/0 group-hover:from-[#2563EB]/30 group-hover:via-cyan-400/20 group-hover:to-blue-600/30 rounded-2xl transition-all duration-500 blur-sm opacity-0 group-hover:opacity-100" />
                <div className="relative hero-glass-card rounded-2xl p-6 md:p-7 h-full transition-all duration-300 group-hover:shadow-[0_20px_50px_-15px_rgba(37,99,235,0.2)]">
                  <motion.div
                    className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#2563EB]/10 to-cyan-500/10 border border-blue-100 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300"
                    animate={{ y: [0, -4, 0] }}
                    transition={{ duration: 3, repeat: Infinity, delay: i * 0.2 }}
                  >
                    <Icon className="w-6 h-6 text-[#2563EB]" />
                  </motion.div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-[#2563EB] transition-colors">{service.name}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed mb-5">{service.desc}</p>
                  <Link to={service.href} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#2563EB] opacity-0 group-hover:opacity-100 transition-all duration-300">
                    Learn more <ArrowRightIcon className="w-4 h-4" />
                  </Link>
                </div>
              </motion.div>
            );
          })}
        </div>

        <div className="text-center mt-14">
          <Link to="/services" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl border-2 border-[#2563EB]/30 text-[#2563EB] font-semibold hover:bg-[#2563EB] hover:text-white transition-all duration-300">
            View All Services <ArrowRightIcon className="w-5 h-5" />
          </Link>
        </div>
      </div>
    </section>
  );
}

export function TechnologiesSection() {
  const [ref, inView] = useInView({ threshold: 0.08, triggerOnce: true });
  const [hovered, setHovered] = useState(null);

  return (
    <section ref={ref} className="py-24 md:py-32 px-6 bg-white relative overflow-hidden">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blue-500/5 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto relative">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}}>
          <SectionHeader
            badge="Next-Gen Stack"
            title="Cutting-Edge"
            highlight="Technologies"
            subtitle="We architect solutions on the world's most advanced cloud, AI, and security platforms."
          />
        </motion.div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
          {techStack.map((tech, i) => (
            <motion.div
              key={tech.name}
              initial={{ opacity: 0, scale: 0.92 }}
              animate={inView ? { opacity: 1, scale: 1 } : {}}
              transition={{ delay: i * 0.05 }}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
              whileHover={{ y: -8, scale: 1.02 }}
              className={`relative rounded-2xl p-5 md:p-6 hero-glass-card border ${tech.border} transition-all duration-300 ${hovered === i ? `shadow-xl ${tech.glow}` : ''}`}
            >
              {hovered === i && (
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-[#2563EB]/5 to-cyan-500/5" />
              )}
              <div className="relative">
                <span className="text-2xl mb-3 block">{tech.icon}</span>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">{tech.category}</p>
                <h3 className="text-base md:text-lg font-bold text-slate-900">{tech.name}</h3>
                <div className="mt-3 h-1 w-8 rounded-full bg-gradient-to-r from-[#2563EB] to-cyan-400 opacity-60 group-hover:w-full transition-all" />
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.4 }}
          className="mt-14 p-8 md:p-10 rounded-3xl relative overflow-hidden border border-slate-200/80 hero-glass-card"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#2563EB]/5 via-cyan-500/5 to-blue-600/5" />
          <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <h3 className="text-xl md:text-2xl font-bold text-slate-900 mb-2">Cloud & AI Architecture</h3>
              <p className="text-slate-600 max-w-xl">Multi-cloud deployments, MLOps pipelines, and zero-trust security woven into every layer of your stack.</p>
            </div>
            <Link to="/services" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#2563EB] to-blue-500 text-white font-semibold rounded-xl hero-glow hover:-translate-y-0.5 transition-all flex-shrink-0">
              Explore Stack <ArrowRightIcon className="w-4 h-4" />
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export function PortfolioSection() {
  const [ref, inView] = useInView({ threshold: 0.06, triggerOnce: true });
  const [filter, setFilter] = useState('all');
  const types = ['all', ...new Set(caseStudies.map((c) => c.type))];
  const filtered = filter === 'all' ? caseStudies : caseStudies.filter((c) => c.type === filter);

  return (
    <section ref={ref} className="py-24 md:py-32 px-6 bg-gradient-to-b from-slate-50/90 via-white to-slate-50/50 relative overflow-hidden">
      <div className="max-w-7xl mx-auto relative">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}}>
          <SectionHeader
            badge="Case Studies"
            title="Our Recent"
            highlight="Projects"
            subtitle="Interactive enterprise solutions — from AI command centers to secure fintech and healthcare platforms."
          />
        </motion.div>

        <div className="flex flex-wrap justify-center gap-2 mb-12">
          {types.map((t) => (
            <button
              key={t}
              onClick={() => setFilter(t)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                filter === t
                  ? 'bg-[#2563EB] text-white shadow-lg shadow-blue-500/20'
                  : 'hero-glass-tag text-slate-600 hover:text-[#2563EB]'
              }`}
            >
              {t === 'all' ? 'All Projects' : t}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 auto-rows-fr">
          {filtered.map((project, i) => (
            <motion.article
              key={project.title}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: i * 0.08 }}
              whileHover={{ y: -8 }}
              className={`group relative rounded-2xl overflow-hidden hero-glass-card border border-slate-200/90 shadow-lg hover:shadow-2xl hover:shadow-blue-500/10 transition-all duration-500 ${project.span}`}
            >
              <div className={`relative overflow-hidden ${project.span.includes('row-span') ? 'min-h-[320px]' : 'h-52'}`}>
                <img src={project.image} alt={project.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/20 to-transparent" />
                <span className="absolute top-4 left-4 px-3 py-1 text-xs font-bold uppercase tracking-wider rounded-full bg-white/90 backdrop-blur text-[#2563EB]">
                  {project.type}
                </span>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-[#2563EB] transition-colors">{project.title}</h3>
                <p className="text-sm text-slate-600 mb-4 line-clamp-2">{project.desc}</p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {project.tags.map((tag) => (
                    <span key={tag} className="px-2.5 py-1 text-xs font-medium rounded-lg bg-slate-100 text-slate-600 border border-slate-200/80">
                      {tag}
                    </span>
                  ))}
                </div>
                <Link to="/portfolio" className="inline-flex items-center gap-2 text-sm font-semibold text-[#2563EB]">
                  View Case Study <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </motion.article>
          ))}
        </div>

        <div className="text-center mt-14">
          <Link to="/portfolio" className="inline-flex items-center gap-2 px-8 py-3.5 bg-gradient-to-r from-[#2563EB] to-blue-500 text-white font-semibold rounded-xl hero-glow hover:-translate-y-0.5 transition-all">
            View All Projects <ArrowRightIcon className="w-5 h-5" />
          </Link>
        </div>
      </div>
    </section>
  );
}
